/**
 * Pure exit-gate helper for the interview container (88 · Task 1.2).
 *
 * The founder-facing interview offers two unambiguous exits:
 *   • "Wrap up"            → complete + background rescore (the `handleFinish` flow)
 *   • "Save & finish later" → leave; the session stays active and resumes next visit
 *
 * `canWrapUp` gates ONLY the "Wrap up" action. It is offered once the founder has
 * had a real exchange (≥ 2 messages: the opener + at least one answer), a real
 * session row exists, and no completion is already in flight (so we never fire a
 * double-complete). "Save & finish later" is always available — there is no
 * dead-end where the founder is stuck in the conversation.
 *
 * Kept as a standalone pure function so the exit logic is unit-tested without
 * dragging the React-Native/reanimated component tree into the test (matches the
 * pure-helper style of `use-interview-session.test.ts`).
 */
export function canWrapUp(
  messageCount: number,
  sessionId: string | null,
  isPending: boolean,
): boolean {
  return messageCount >= 2 && !!sessionId && !isPending;
}

/**
 * Whether the top-right "Wrap up" header action should be RENDERED at all
 * (Task 1.2-R: the action APPEARS only when ready, rather than showing disabled
 * from turn one). It reuses `canWrapUp` for the "ready" gate, and additionally
 * stays visible while a completion is in flight so the button can show its
 * "Wrapping up…" state instead of vanishing mid-tap (`canWrapUp` goes false the
 * instant `isPending` flips true). The button itself is still disabled while
 * pending — visibility and enablement are deliberately separate concerns.
 */
export function shouldShowWrapUp(
  messageCount: number,
  sessionId: string | null,
  isPending: boolean,
): boolean {
  return isPending || canWrapUp(messageCount, sessionId, isPending);
}
